import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import StepperVertical from "./StepperVertical";
import styles from "../../../_css/AddRoleplay.module.css"
import PrivacyPolicyModal from "../../Modals/PrivacyPolicieyModal";
import Scenario from "./Scenario";
import AvatarDifficulty from "./AvatarDifficulty";
import { deleteAuthToken } from "../../../utils/helper";
import RoleSelection from "./RoleSelection";
import GoalsAndCompentencies from "./GoalAndCompetencies";
import { createRoleplays, deleteRoleplays, publishPreDefinedRoleplays, updatePredefinedRoleplaysStepData, uploadImageToScenario } from "../../../requests";
import { notifyError, notifyInfo, notifySuccess } from "../../helper/Toaster"
import PreviewAndStart from "./PreviewAndStart";
import UploadDocuments from "./UploadDocuments";

const AddRoleplayIndex = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [stepData, setStepData] = useState({});
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const [rolePlayId, setRolePlayId] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState("English");

    // NEW: Loading state
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const userInfo = useSelector((state) => state.user);
    const rolePlayIdRef = useRef(null);

    const stepKeys = {
        1: "Define the roles",
        2: "Describe Your Scenario",
        3: "Avatar Behaviour & Difficulty level",
        4: "Learning Goals & Competencies",
        5: "Upload Documents",
        6: "Preview & Start",
    };

    // Initialize ID from location state
    useEffect(() => {
        if (location.state?.custom_rolepayid) {
            const roleplayIdFromState = location.state.custom_rolepayid;
            setRolePlayId(roleplayIdFromState);
            rolePlayIdRef.current = roleplayIdFromState;
        }
    }, [location.state]);
    useEffect(() => {
        const getLang = () => localStorage.getItem("selected_language") || "English";

        // Set initial value
        setSelectedLanguage(getLang());

        // Listen for changes (works across tabs too)
        const handleStorageChange = (event) => {
            if (event.key === "selected_language") {
                setSelectedLanguage(event.newValue || "English");
            }
        };

        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
        };
    }, []);


    // Fetch existing data if ID exists
    // useEffect(() => {
    //     if (!rolePlayId) return;

    //     const fetchRoleplayData = async () => {
    //         try {
    //             // --- API COMMENTED OUT ---
    //             // const res = await get_customized_roleplay_details({ id: rolePlayId });
    //             // const data = res.data;
    //             // if (!data) return;

    //             // // Safely parse JSON data
    //             // const safeParse = (str, fallback) => {
    //             //     try {
    //             //         return str ? JSON.parse(str) : fallback;
    //             //     } catch (e) {
    //             //         return fallback;
    //             //     }
    //             // };

    //             // const savedStepData = safeParse(data.steps_data, {});
    //             // const savedCompletedSteps = safeParse(data.completed_steps, []);

    //             // setStepData((prev) => (Object.keys(prev).length === 0 ? savedStepData : prev));

    //             // const lastStep = data.current_step > 0 ? data.current_step : 1;
    //             // setCurrentStep(lastStep);

    //             // setCompletedSteps((prev) => {
    //             //     if (prev.length === 0) return savedCompletedSteps;
    //             //     return [...new Set([...prev, ...savedCompletedSteps])];
    //             // });
    //             console.log("Skipping data fetch for rolePlayId:", rolePlayId);

    //         } catch (err) {
    //             console.error("Failed to fetch customized roleplay data:", err);
    //         }
    //     };

    //     fetchRoleplayData();
    // }, [rolePlayId]);

    const markStepComplete = (step) => {
        setCompletedSteps((prev) => {
            if (prev.includes(step)) return prev;
            return [...prev, step];
        });
    };

    const handleNext = async (data) => {
        // 1. Start Loader
        setIsLoading(true);

        try {
            // Update local state first
            const updatedStepData = { ...stepData, [`step${currentStep}`]: data };
            setStepData(updatedStepData);

            if (currentStep === 1) {
                // --- STEP 1: CREATE ROLEPLAY ---
                if (!rolePlayIdRef.current) {
                    const createResponse = await createRoleplays({
                        data: {
                            ...data,
                            selected_language: selectedLanguage
                        }
                    });

                    if (createResponse && createResponse.success) {
                        const roleplayid = createResponse.roleplay_id;
                        setRolePlayId(roleplayid);
                        rolePlayIdRef.current = roleplayid;

                        notifySuccess(createResponse.message || "Roleplay created successfully");
                        markStepComplete(currentStep);
                        setCurrentStep((prev) => prev + 1);
                    } else {
                        notifyError(createResponse?.message || "Failed to create roleplay.");
                    }
                } else {
                    // Editing Step 1 (ID exists) -> Call Update API if needed, else just move next
                    markStepComplete(currentStep);
                    setCurrentStep((prev) => prev + 1);
                }
            }
            else if (currentStep === 2 || currentStep === 3 || currentStep === 4) {

                if (!rolePlayIdRef.current) {
                    notifyError("Roleplay ID missing. Please complete Step 1.");
                    setIsLoading(false);
                    return;
                }

                // Construct payload based on step
                let payload = {};

                if (currentStep === 2) {
                    const {
                        conversationContext, personInvolved, previousEvents,
                        challengingAspects, potentialConsequences, scenario_description
                    } = data;

                    const combinedText = scenario_description || [
                        conversationContext, personInvolved, previousEvents,
                        challengingAspects, potentialConsequences,
                    ].filter(Boolean).join(" ");

                    payload = {
                        roleplay_id: rolePlayIdRef.current,
                        step_number: 2,
                        scenario_description: combinedText,
                        selected_language: selectedLanguage
                    };
                }
                else if (currentStep === 3) {
                    payload = {
                        roleplay_id: rolePlayIdRef.current,
                        step_number: 3,
                        avatar_behaviour: data.avatar_behaviour, // Adjust key based on your Component's data structure
                        difficulty_level: data.difficulty_level,
                        selected_language: selectedLanguage
                    };
                }
                else if (currentStep === 4) {
                    payload = {
                        roleplay_id: rolePlayIdRef.current,
                        step_number: 4,
                        learning_goals: data.learning_goals, // Adjust key based on your Component's data structure
                        competencies: data.competencies,
                        selected_language: selectedLanguage
                    };
                }

                // --- CALL UPDATE API ---
                const updateResponse = await updatePredefinedRoleplaysStepData(payload);

                if (updateResponse && updateResponse.success) {
                    // Success
                    notifySuccess(updateResponse.message || `Step ${currentStep} saved successfully`);
                    markStepComplete(currentStep);
                    setCurrentStep((prev) => prev + 1);
                } else {
                    // Failure
                    notifyError(updateResponse?.message || `Failed to save Step ${currentStep}`);
                    // Do NOT move next
                }
            }
            else {
                // --- STEP 5 & 6 (Default Behavior) ---
                markStepComplete(currentStep);
                if (currentStep < 6) setCurrentStep((prev) => prev + 1);
            }

        } catch (err) {
            console.error("Error in handleNext:", err);
            notifyError(err?.response?.data?.message || err?.message || "Something went wrong.");
        } finally {
            // 2. Stop Loader
            setIsLoading(false);
        }
    };

    const publishingRoleplay = async ({ updated_title }) => {
        try {
            const params = {
                roleplay_id: rolePlayIdRef.current,
                action: "publish",
                change_title: updated_title
            };
            // --- API COMMENTED OUT ---
            const response = await publishPreDefinedRoleplays(params);
            return { success: true };
        } catch (error) {
            console.error("Failed to publish role play.");
        }
    };

    const deleteRoleplay = async () => {
        try {
            const params = {
                user_id: userInfo?.userId,
                roleplay_id: rolePlayIdRef.current,
            };
            // --- API COMMENTED OUT ---
            return await deleteRoleplays(params);
        } catch (error) {
            console.error("Failed to delete role play.");
        }
    };

    const handleUpload = async (formData) => {
        try {
            // Append the ID here so the Child doesn't need to know about it
            formData.append("roleplay_id", rolePlayIdRef.current);
            const response = await uploadImageToScenario(formData);
            return response;

        } catch (err) {
            console.error("Upload failed in parent:", err);
            return {
                data: {
                    success: false,
                    message: err?.response?.data?.message || err.message || "Upload failed"
                }
            };
        }
    };
    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep((prev) => prev - 1);
        }
    };

    const handleFinalSubmit = async () => {
        const selectedInfo = stepData.step1;

        if (!userInfo?.tncStatus) {
            setShowPrivacyModal(true);
        } else {
            try {
                const rolePlayEndpoint = selectedInfo === "HR Specialist" ? "hr" : "sales";
                navigate("/chat", {
                    state: {
                        selectedInfo: {
                            roleplayName: selectedInfo,
                            preferredLanguage: "English",
                            rolePlayEndpoint,
                        },
                        roleplayDescription: selectedInfo,
                        roleplay_id: rolePlayIdRef.current,
                    },
                });
            } catch (error) {
                if (error?.response?.status === 401) {
                    // deleteAuthToken();
                    // window.location.reload();
                }
                console.error("Error in submitting roleplay:", error);
            }
        }
    };

    const updateSelection = (step, value) => {
        setStepData((prev) => ({
            ...prev,
            [`step${step}`]: value,
        }));
    };

    const goToStep = (step) => {
        const isStep1Completed = completedSteps.includes(1);
        if (step === 1 || isStep1Completed) {
            setCurrentStep(step);
        }
    };

    const clearSelectionForStep = (step) => {
        setStepData((prev) => {
            const updated = { ...prev };
            delete updated[`step${step}`];
            return updated;
        });
    };

    const renderStepComponent = () => {
        const commonProps = {
            handleNext,
            handleBack,
            step: currentStep,
            markStepComplete,
            previousSelections: stepData,
            updateSelection,
            clearSelectionForStep,
            roleplay_id: rolePlayIdRef.current,
            isLoading,
            deleteRoleplay,
            publishingRoleplay,
            handleUpload,
            language: selectedLanguage || "English"
        };

        switch (currentStep) {
            case 1:
                return <RoleSelection {...commonProps} />;
            case 2:
                return <Scenario {...commonProps} />;
            case 3:
                return <AvatarDifficulty {...commonProps} rolePlayId={rolePlayId} goToStep={goToStep} />;
            case 4:
                return <GoalsAndCompentencies {...commonProps} />;
            case 5:
                return <UploadDocuments {...commonProps} />;
            case 6:
                return <PreviewAndStart {...commonProps} />

            default:
                return <div className={styles.stepCard}>Step {currentStep} Content Placeholder</div>;
        }
    };

    return (
        <div className={styles.layoutContainer}>
            <div className={styles.stepperArea}>
                <StepperVertical
                    currentStep={currentStep}
                    completedSteps={completedSteps}
                    onStepClick={goToStep}
                />
            </div>

            <div className={styles.contentArea}>
                {renderStepComponent()}
            </div>

            <PrivacyPolicyModal
                show={showPrivacyModal}
                onHide={() => setShowPrivacyModal(false)}
                onSubmit={handleFinalSubmit}
            />
        </div>
    );
};

export default AddRoleplayIndex;